import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { truthyOrZero } from '@/helpers/math';

// The slider is not an input range but a series of divs.
// Therefore we calculate the current and max slider thumb position
// using cursor/touch position relative to the slider div
const calculateSliderPositionValues = ({
  e,
  sliderRef,
  leftAndRightPadding,
  vertical = false
}) => {
  let sliderOffset;
  let sliderLength;
  let cursorPosition;
  let relativePosition;

  if (vertical) {
    // Calculate the distance between the browser edge and the start of the slider (also slider thumb position 0)
    sliderOffset = sliderRef.current.getBoundingClientRect().top + leftAndRightPadding;
    // Calculate the height of the slider element (also the max slide thumb position)
    sliderLength = sliderRef.current.offsetHeight - (leftAndRightPadding * 2);
    // Calculate the current mouse/touch position
    cursorPosition = e.clientY;
    // Calculate the Y position relative to the slider offset
    relativePosition = sliderLength - (cursorPosition - sliderOffset);
  } else {
    // Calculate the distance between the browser edge and the start of the slider (also slider thumb position 0)
    sliderOffset = sliderRef.current.offsetLeft + leftAndRightPadding;
    // Calculate the width of the slider element (also the max slide thumb position)
    sliderLength = sliderRef.current.offsetWidth - (leftAndRightPadding * 2);
    // Calculate the current mouse/touch position
    cursorPosition = e.clientX;
    // Calculate the X position relative to the slider offset
    relativePosition = cursorPosition - sliderOffset;
  }

  return {
    maximumPosition: sliderLength,
    pos: relativePosition
  };
};

// Calculate the seek (in seconds) using slider position
// (pos/max) as a proportion of the total duration
const calculateSeek = (pos, max, durationSeconds) => {
  let position = (pos / max) * durationSeconds;

  if (position < 0) { position = 0; }
  if (position > durationSeconds) { position = durationSeconds; }
  if (Math.abs(durationSeconds - position) < 1) { position = durationSeconds; }

  return position;
};

// This hook contains all state logic for the progress slider
const useProgressBarState = ({
  seek,
  onSeek,
  max,
  leftAndRightPadding,
  vertical = false,
  forceStartPosition,
  alwaysShowThumb = false
}) => {
  // Reference the slider div to track clicks, touches and drags
  const sliderRef = useRef(null);

  // ----- STATE -----

  // The current position of the slider
  // Lowest is 0 and the max is dependent on the width of the screen
  const [position, setPosition] = useState(seek);
  
  // The start position of any click or drag on the slider
  const [dragStartPosition, setStartPostition] = useState(null);

  // Track whether the user dragged the slider or just clicked/tapped
  const [dragged, setDragged] = useState(false);

  // Track whether the interaction is a touch or click interaction
  // This is to stop click handlers firing on touch events
  const [isTouch, setIsTouch] = useState(false);

  // ----- COMMON EVENT HANDLERS -----

  // Handles the initiation of a touch/click/drag by setting its start position
  const handleStart = (e) => {
    const { maximumPosition, pos } = calculateSliderPositionValues({
      e,
      sliderRef,
      leftAndRightPadding,
      vertical
    });

    const startPosition = calculateSeek(pos, maximumPosition, max);

    setStartPostition(startPosition);
    setPosition(startPosition);
  };

  // Handles any dragging of the slider
  const handleDragSlider = useCallback((e) => {
    // Only consider this a drag if a start position has been set.
    // This start position will only be set if the user started their
    // click/drag inside the touch zone
    if (truthyOrZero(dragStartPosition)) {
      if (!dragged) { setDragged(true); }

      const { pos, maximumPosition } = calculateSliderPositionValues({
        e,
        sliderRef,
        leftAndRightPadding,
        vertical
      });

      // Update the position as the user drags
      setPosition(calculateSeek(pos, maximumPosition, max));
    }
    // eslint-disable-next-line
  }, [dragStartPosition, dragged]);

  // Handles the end of touch/click/drag
  const handleEnd = useCallback(() => {
    if (truthyOrZero(dragStartPosition)) {
      if (!dragged) {
        // If the user has not dragged then this is just a click/tap.
        // We can therefore use the start position
        setPosition(dragStartPosition);
        onSeek(dragStartPosition);
      } else {
        // Else we should use the current position updated by handleDragSlider
        setPosition(position);
        onSeek(position);
      }
  
      // Reset the start position and drag state
      setStartPostition(null);
      setDragged(false);
    }
    // eslint-disable-next-line
  }, [dragStartPosition, dragged, position]);

  // ----- CLICK EVENT HANDLERS -----

  const handleClickStart = (e) => {
    e.preventDefault();

    // Only fire this event if the interaction isn't touch
    if (!isTouch) {
      handleStart(e);
    }
  };

  const handleClickMove = useCallback((e) => {
    // THIS WAS MESSING WITH DRAGGABLE ELEMENTS
    // e.preventDefault();

    // Only fire this event if the interaction isn't touch
    if (!isTouch) {
      handleDragSlider(e);
    }
  }, [handleDragSlider, isTouch]);

  const handleClickEnd = useCallback((e) => {
    // THIS WAS MESSING WITH DRAGGABLE ELEMENTS
    // e.preventDefault();

    if (isTouch) {
      // handleClickEnd is the last event fired so reset isTouch flag
      setIsTouch(false);
    } else {
      handleEnd(e);
    }
  }, [handleEnd, isTouch]);

  // ----- TOUCH EVENT HANDLERS -----

  const handleTouchStart = (e) => {
    // Mark the interaction as touch to stop click handlers being fired as well
    setIsTouch(true);
    handleStart(e.touches[0]);
  };

  const handleTouchMove = (e) => {
    handleDragSlider(e.touches[0]);
  };

  const handleTouchEnd = (e) => {
    handleEnd(e.touches[0]);
  };

  // SUPPORT MOUSE MOVEMENT LEAVING THE SLIDER
  // The user must initiate a click drag within the touch zone (on and around the slider)
  // but can move the mouse anywhere on the screen whilst dragging.
  // Therefore mouse 'move' and 'up' listeners are attached to the document,
  // and only the mouse 'down' listener is attached to the slider
  useEffect(() => {
    document.addEventListener('mousemove', handleClickMove);
    document.addEventListener('mouseup', handleClickEnd);

    return () => {
      document.removeEventListener('mousemove', handleClickMove);
      document.removeEventListener('mouseup', handleClickEnd);
    };
  }, [handleClickMove, handleClickEnd]);

  // The current position is the seek time unless the user is dragging, whereby position overrides
  const currentPosition = (truthyOrZero(dragStartPosition))
    ? position
    : seek;

  const perc = max ? ((currentPosition / max) * 100) : 0;
  const sliderProgressPercentage = (perc > 100) ? 100 : perc;

  const progressStyles = useMemo(() => ({
    ...(!vertical && { width: `${ forceStartPosition ? 0 : sliderProgressPercentage }%` }),
    ...(vertical && { height: `${ forceStartPosition ? 0 : sliderProgressPercentage }%` })
  }), [forceStartPosition, sliderProgressPercentage, vertical]);

  const thumbStyles = useMemo(() => ({
    ...(!vertical && { left: `calc(${ forceStartPosition ? 0 : sliderProgressPercentage }% - 6px)` }),
    ...(vertical && { bottom: `calc(${ forceStartPosition ? 0 : sliderProgressPercentage }% - 6px)` }),
    visibility: (alwaysShowThumb || ((truthyOrZero(dragStartPosition)) && dragged)) ? 'visible' : 'hidden'
  }), [alwaysShowThumb, sliderProgressPercentage, dragStartPosition, dragged, forceStartPosition, vertical]);

  return {
    handleClickStart,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    sliderRef,
    liveSliderPercentage: sliderProgressPercentage,
    liveSliderSeek: currentPosition,
    dragged,
    progressStyles,
    thumbStyles
  };
};

export default useProgressBarState;